import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { setAuthErrorHandler, setTokenGetter } from '@/api/client';
import { setUnauthorizedHandler } from '@/api/handleResponse';
import { sessionEndedLoginPath } from '@/auth/sessionEnded';
import { router } from '@/router';
import { useCurrentUser } from '@/api/user';

/**
 * Bridges the React-only `getAccessTokenSilently` into the plain-function API
 * layer (see app/api/client.ts), and keeps Sentry's user scope in sync.
 *
 * Renders nothing. Must live inside <Auth0Provider>.
 */
export function AuthBridge() {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const { data: backendUser } = useCurrentUser();

    useEffect(() => {
        setTokenGetter(() => getAccessTokenSilently());
    }, [getAccessTokenSilently]);

    useEffect(() => {
        // A 401 from the backend means the session is no longer valid; a token
        // acquisition failure means silent renewal gave up. Both land on the
        // app's own login page, keeping where the user was — NOT straight on
        // Auth0: see sessionEnded.ts for why an unattended Auth0 redirect
        // ends in "we couldn't find your session".
        const sessionEnded = () => {
            const { pathname, search } = window.location;
            if (pathname.startsWith('/login')) return;
            router.navigate(sessionEndedLoginPath(pathname, search), {
                replace: true,
            });
        };
        setUnauthorizedHandler(sessionEnded);
        setAuthErrorHandler(sessionEnded);
    }, []);

    useEffect(() => {
        if (backendUser) {
            // Identify the user by name (Sentry shows `username`) rather than
            // the opaque user id; keep email for traceability.
            Sentry.setUser({
                email: backendUser.email,
                username: `${backendUser.firstName} ${backendUser.lastName}`,
            });
        } else if (!isAuthenticated) {
            Sentry.setUser(null);
        }
    }, [isAuthenticated, backendUser]);

    return null;
}
